// src/features/secret/TransitPanel.tsx
// Transit (encryption-as-a-service) engine workspace — key CRUD + rotate, and an operations
// playground (encrypt/decrypt/rewrap/sign/verify/data-key/random) for a selected key.
// See docs/ux/secret-ia.md section 1d.

import { useEffect, useState, useCallback } from 'react';
import { useI18n, useSecret } from '@react/hooks';
import { Button, Input, Textarea, Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { StatusBadge } from '@/components/StatusBadge';
import { TypedConfirmDialog } from './TypedConfirmDialog';
import { FormDialog, FormRow } from '@/components/form';
import type { EncryptionKey, EncryptionKeyType } from '@core/types/secret';
import { EncryptionKeyTypeValues } from '@core/types/secret';
import { encryptionKeyStatusVariant, canDeleteEncryptionKey, enumSuffix } from './logic';

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);
  return { copied, copy };
}

const KEY_TYPES = EncryptionKeyTypeValues.filter((v) => v !== 'ENCRYPTION_KEY_TYPE_UNSPECIFIED');

function keyTypeLabelKey(type?: EncryptionKeyType): string {
  return `secret.transit.keyType.${enumSuffix(type, 'ENCRYPTION_KEY_TYPE_')}`;
}

function CreateKeyForm({ open, engineId, onCreated, onClose }: { open: boolean; engineId: string; onCreated: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const store = useSecret();
  const [name, setName] = useState('');
  const [keyType, setKeyType] = useState<EncryptionKeyType>('ENCRYPTION_KEY_TYPE_AES256_GCM96');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const dirty = !!name.trim();

  const reset = () => {
    setName('');
    setKeyType('ENCRYPTION_KEY_TYPE_AES256_GCM96');
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.CreateEncryptionKey(engineId, { engine_id: engineId, key: { name: name.trim(), key_type: keyType } as EncryptionKey });
      reset();
      onCreated();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('common.create')}
      canSubmit={!!name.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('secret.transit.createKey')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <Input label={t('secret.transit.field.keyName')} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
        <Select
          label={t('secret.transit.field.keyType')}
          value={keyType}
          onChange={(e) => setKeyType(e.target.value as EncryptionKeyType)}
          options={KEY_TYPES.map((v) => ({ value: v, label: t(keyTypeLabelKey(v)) }))}
        />
      </div>
    </FormDialog>
  );
}

// --- Operations playground ---

type Op = 'encrypt' | 'decrypt' | 'rewrap' | 'sign' | 'verify' | 'datakey' | 'random';
const OPS: Op[] = ['encrypt', 'decrypt', 'rewrap', 'sign', 'verify', 'datakey', 'random'];

function OperationsPanel({ engineId, keyName }: { engineId: string; keyName: string }) {
  const { t } = useI18n();
  const store = useSecret();
  const [op, setOp] = useState<Op>('encrypt');
  const [input, setInput] = useState('');
  const [secondary, setSecondary] = useState(''); // ciphertext-for-decrypt, signature-for-verify
  const [bytes, setBytes] = useState('32');
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  const isSensitiveResult = op === 'decrypt' || op === 'datakey' || op === 'random';

  const reset = () => { setResult(null); setRevealed(false); setError(null); };

  const run = async () => {
    setIsRunning(true);
    setError(null);
    try {
      switch (op) {
        case 'encrypt': {
          const r = await store.Encrypt(engineId, keyName, { engine_id: engineId, key_name: keyName, plaintext: input });
          setResult(r.result?.ciphertext ?? '');
          break;
        }
        case 'decrypt': {
          const r = await store.Decrypt(engineId, keyName, { engine_id: engineId, key_name: keyName, ciphertext: input });
          setResult(r.result?.plaintext ?? '');
          break;
        }
        case 'rewrap': {
          const r = await store.Rewrap(engineId, keyName, { engine_id: engineId, key_name: keyName, ciphertext: input });
          setResult(r.result?.ciphertext ?? '');
          break;
        }
        case 'sign': {
          const r = await store.Sign(engineId, keyName, { engine_id: engineId, key_name: keyName, input });
          setResult(r.result?.signature ?? '');
          break;
        }
        case 'verify': {
          const r = await store.Verify(engineId, keyName, { engine_id: engineId, key_name: keyName, input, signature: secondary });
          setResult(r.result?.valid ? t('secret.transit.verifyValid') : t('secret.transit.verifyInvalid'));
          break;
        }
        case 'datakey': {
          const r = await store.GenerateDataKey(engineId, keyName, { engine_id: engineId, key_name: keyName, type: 'plaintext' });
          setResult(r.result?.plaintext || r.result?.ciphertext || '');
          break;
        }
        case 'random': {
          const r = await store.GenerateRandomBytes(engineId, { engine_id: engineId, bytes: parseInt(bytes, 10), format: 'base64' });
          setResult(r.data);
          break;
        }
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>{t('secret.transit.operationsTitle', { key: keyName })}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
        <div role="tablist" className="flex flex-wrap gap-1">
          {OPS.map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={op === v}
              onClick={() => { setOp(v); reset(); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border ${op === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-foreground/20'}`}
            >
              {t(`secret.transit.op.${v}`)}
            </button>
          ))}
        </div>

        {(op === 'encrypt' || op === 'sign') && (
          <Textarea label={t('secret.transit.field.plaintextBase64')} value={input} onChange={(e) => setInput(e.target.value)} rows={3} />
        )}
        {(op === 'decrypt' || op === 'rewrap') && (
          <Textarea label={t('secret.transit.field.ciphertext')} value={input} onChange={(e) => setInput(e.target.value)} rows={3} />
        )}
        {op === 'verify' && (
          <>
            <Textarea label={t('secret.transit.field.plaintextBase64')} value={input} onChange={(e) => setInput(e.target.value)} rows={2} />
            <Textarea label={t('secret.transit.field.signature')} value={secondary} onChange={(e) => setSecondary(e.target.value)} rows={2} />
          </>
        )}
        {op === 'random' && (
          <Input label={t('secret.transit.field.bytes')} type="number" min={1} max={1024} value={bytes} onChange={(e) => setBytes(e.target.value)} />
        )}

        <Button size="sm" onClick={run} disabled={isRunning}>
          {isRunning ? t('common.loading') : t(`secret.transit.op.${op}`)}
        </Button>

        {result != null && (
          <div className="rounded-md bg-muted/40 p-2">
            {isSensitiveResult && !revealed ? (
              <Button variant="ghost" size="sm" onClick={() => setRevealed(true)}>{t('secret.kv.reveal')}</Button>
            ) : (
              <div className="flex items-start gap-2">
                <code className="flex-1 break-all text-xs font-mono select-all">{result}</code>
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => copy(result)}>
                  {copied ? t('common.copied') : t('common.copy')}
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Key configuration (GetEncryptionKey + UpdateEncryptionKeyConfig) ---

function KeyConfigForm({ engineId, keyName, onSaved }: { engineId: string; keyName: string; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useSecret();
  const [key, setKey] = useState<EncryptionKey | null>(null);
  const [exportable, setExportable] = useState(false);
  const [deletionAllowed, setDeletionAllowed] = useState(false);
  const [autoRotateDays, setAutoRotateDays] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    store.GetEncryptionKey(engineId, keyName).then((r) => {
      const k = r.key ?? null;
      setKey(k);
      if (k) {
        setExportable(k.exportable);
        setDeletionAllowed(k.deletion_allowed);
        const days = Math.round((k.auto_rotate_seconds ?? 0) / 86400);
        setAutoRotateDays(days > 0 ? String(days) : '');
      }
    }).catch(() => setKey(null));
  }, [engineId, keyName, store]);

  const dirty = !!key && (
    exportable !== key.exportable
    || deletionAllowed !== key.deletion_allowed
    || autoRotateDays !== (Math.round((key.auto_rotate_seconds ?? 0) / 86400) > 0 ? String(Math.round((key.auto_rotate_seconds ?? 0) / 86400)) : '')
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsed = autoRotateDays.trim() === '' ? 0 : parseInt(autoRotateDays, 10);
      await store.UpdateEncryptionKeyConfig(engineId, keyName, {
        engine_id: engineId,
        key_name: keyName,
        exportable,
        deletion_allowed: deletionAllowed,
        auto_rotate_seconds: (Number.isNaN(parsed) ? 0 : parsed) * 86400,
        update_mask: ['exportable', 'deletion_allowed', 'auto_rotate_seconds'],
      });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  if (!key) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;

  return (
    <div className="rounded-lg border border-border p-3">
      <p className="pb-1 text-xs text-muted-foreground">
        {t('secret.transit.version', { v: key.latest_version })} · min decrypt v{key.min_decryption_version} · min encrypt v{key.min_encryption_version}
      </p>
      <FormRow label={t('secret.transit.field.exportable')} labelWidth="sm">
        <input type="checkbox" checked={exportable} onChange={(e) => setExportable(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" aria-label={t('secret.transit.field.exportable')} />
      </FormRow>
      <FormRow label={t('secret.transit.field.deletionAllowed')} labelWidth="sm">
        <input type="checkbox" checked={deletionAllowed} onChange={(e) => setDeletionAllowed(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" aria-label={t('secret.transit.field.deletionAllowed')} />
      </FormRow>
      <FormRow label={t('secret.transit.field.autoRotateDays')} hint={t('secret.transit.field.autoRotateDaysHelp')} labelWidth="sm">
        <Input
          value={autoRotateDays}
          onChange={(e) => setAutoRotateDays(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder={t('secret.transit.field.autoRotateDaysPlaceholder')}
          inputMode="numeric"
          fullWidth={false}
          className="w-24"
        />
      </FormRow>
      <div className="flex justify-end pt-1">
        <Button size="sm" onClick={handleSave} disabled={isSaving || !dirty}>{isSaving ? t('common.saving') : t('common.save')}</Button>
      </div>
    </div>
  );
}

// --- Top-level ---

export function TransitPanel({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const store = useSecret();
  const [keys, setKeys] = useState<EncryptionKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EncryptionKey | null>(null);
  const [isActing, setIsActing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    store.ListEncryptionKeys(engineId).then((r) => setKeys(r.keys ?? [])).catch(() => setKeys([])).finally(() => setLoading(false));
  }, [engineId, store]);

  useEffect(() => { load(); }, [load]);

  const doRotate = async (name: string) => {
    setIsActing(true);
    try { await store.RotateEncryptionKey(engineId, name, { engine_id: engineId, key_name: name }); load(); } finally { setIsActing(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setIsActing(true);
    try { await store.DeleteEncryptionKey(engineId, deleteTarget.name); setDeleteTarget(null); if (selected === deleteTarget.name) setSelected(null); load(); } finally { setIsActing(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('secret.transit.keysTitle')}</h2>
        <Button size="sm" onClick={() => setShowCreate(true)}>{t('secret.transit.createKey')}</Button>
      </div>

      <CreateKeyForm open={showCreate} engineId={engineId} onCreated={() => { setShowCreate(false); load(); }} onClose={() => setShowCreate(false)} />

      <TypedConfirmDialog
        open={!!deleteTarget}
        title={t('secret.transit.deleteKeyTitle')}
        description={t('secret.transit.deleteKeyDescription')}
        phrase={deleteTarget?.name ?? ''}
        confirmLabel={t('common.delete')}
        isConfirming={isActing}
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('secret.transit.noKeys')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {keys.map((key) => (
            <li key={key.name} className="p-3">
              <div className="flex items-center justify-between">
                <button onClick={() => setSelected(selected === key.name ? null : key.name)} className="text-left">
                  <p className="text-sm font-medium text-foreground">{key.name}</p>
                  <p className="text-xs text-muted-foreground">{t(keyTypeLabelKey(key.key_type))} · {t('secret.transit.version', { v: key.latest_version })}</p>
                </button>
                <div className="flex items-center gap-2">
                  <StatusBadge variant={encryptionKeyStatusVariant(key.status)} label={t(`secret.transit.status.${enumSuffix(key.status, 'ENCRYPTION_KEY_STATUS_')}`)} size="sm" />
                  <Button variant="ghost" size="sm" onClick={() => setConfiguring(configuring === key.name ? null : key.name)}>{t('secret.transit.configure')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => doRotate(key.name)} disabled={isActing}>{t('secret.transit.rotate')}</Button>
                  {canDeleteEncryptionKey(key) && (
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteTarget(key)}>{t('common.delete')}</Button>
                  )}
                </div>
              </div>
              {configuring === key.name && (
                <div className="mt-3">
                  <KeyConfigForm engineId={engineId} keyName={key.name} onSaved={() => { setConfiguring(null); load(); }} />
                </div>
              )}
              {selected === key.name && (
                <div className="mt-3">
                  <OperationsPanel engineId={engineId} keyName={key.name} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
